import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import { Form, Formik } from "formik";
import { merge } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import ChildStep4Schema from "./ChildStep4Schema";
import * as actions from "../../../store/modules/register/registerActions";
import { RootState } from "../../../store/reducers";
import { IChild, IRegistrationMedicalPlanAnswer } from "../../../types/types";
import { getStyleColor } from "../../../utils/helpers";
import MedicalConditions from "../../Children/MedicalConditions";
import { ButtonPrimary, ButtonSecondary } from "../../Common/Buttons/Buttons";
import Pager from "../../Common/Pager/Pager";

interface Props {
  child: IChild;
  id: string;
}

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "white",
  },
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  formLegend: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: theme.spacing(4),
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(2),
  },
  formLabel: {
    marginBottom: theme.spacing(2),
  },
  heading: {
    marginTop: 0,
    marginBottom: 0,
  },
  subHeading: {
    fontSize: "16px",
    color: getStyleColor("--custom-color-dark-grey"),
  },
  formControl: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  selectEmpty: {
    marginBottom: theme.spacing(2),
  },
  textField: { marginBottom: theme.spacing(2) },
  container: { paddingLeft: 0 },
}));

function FormChildStep4({ child, id }: Props) {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const planTemplate = useSelector((state: RootState) => state?.commonData?.data?.medicalPlanTemplate);

  function updateChild(values: IChild) {
    if (values.hasMedicalConditions === false) {
      values.medicalConditions = [];
    }

    actions.saveChild(dispatch, values);
    if (values.hasMedicalConditions) {
      navigate(`/registration-child-step/5/${id}/0`);
    } else {
      navigate(`/registration-child-step/6/${id}`);
    }
  }

  var medicalConditions =
    child.medicalPlanAnswers !== undefined
      ? child.medicalPlanAnswers.map((x) => x.conditionId).filter((value, index, self) => self.indexOf(value) === index)
      : [];

  const formikValues = {
    hasMedicalConditions: undefined,
    medicalConditions: medicalConditions,
    medicalPlanAnswers: new Array<IRegistrationMedicalPlanAnswer>(),
  };
  const initialValues = merge(formikValues, child);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ChildStep4Schema}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          setSubmitting(false);
          updateChild(values);
        }, 500);
      }}
    >
      {({ setFieldValue, submitForm, isSubmitting, values, touched, errors, handleChange, handleBlur }) => (
        <Form>
          <Grid container className={classes.root} spacing={6}>
            <Grid item xs={12} md={6}>
              <MedicalConditions
                classes={classes}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                touched={touched}
                values={values}
                errors={errors}
                dispatch={dispatch}
                medicalConditions={planTemplate?.conditions}
                selectedMedicalConditions={values.medicalPlanAnswers?.map((x) => x.conditionId)}
              />
            </Grid>
          </Grid>
          <Pager>
            <ButtonSecondary href={`/registration-child-step/3/${id}`}>
              <strong>Back</strong>
            </ButtonSecondary>
            <ButtonPrimary disabled={isSubmitting} onClick={submitForm}>
              <strong>Next</strong>
            </ButtonPrimary>
          </Pager>
          <Backdrop className={classes.backdrop} open={isSubmitting}>
            <CircularProgress color="primary" />
          </Backdrop>
        </Form>
      )}
    </Formik>
  );
}

export default FormChildStep4;
